import { enums } from "@shared/constants";
import { SITE_URL } from "@shared/env";
import { StateTokenService } from "./StateTokenService";

interface AuthUrlOptions {
  stateToken?: string;
  nextUrl?: string;
  clientId?: string;
  redirectUri?: string;
  scope?: string;
  accessType?: string;
}

export class GoogleOAuthService {
  private static DEFAULT_CLIENT_ID = enums.GOOGLE_OAUTH_MAP.CLIENT_ID.key_value;

  private static DEFAULT_REDIRECT_URI = `${SITE_URL}accounts/google/login/callback/`;

  private static DEFAULT_SCOPE = encodeURIComponent("openid email profile");

  private static DEFAULT_ACCESS_TYPE = "offline";

  public static AUTH_CHANNEL = "google_auth_channel";

  public static MESSAGES = {
    USER_AUTHENTICATED: "UserAuthenticated",
  };

  /**
   * Builds the Google OAuth2 URL for authentication.
   *
   * @param {AuthUrlOptions} options - Optional parameters to customize the URL.
   * @returns {string} The constructed OAuth URL.
   */
  static buildAuthUrl(options: AuthUrlOptions = {}): string {
    const stateToken = options.stateToken || StateTokenService.create();
    const nextUrl = options.nextUrl || "/";
    const clientId = options.clientId || this.DEFAULT_CLIENT_ID;
    const redirectUri = options.redirectUri || this.DEFAULT_REDIRECT_URI;
    const scope = options.scope || this.DEFAULT_SCOPE;
    const accessType = options.accessType || this.DEFAULT_ACCESS_TYPE;
    const STATE = encodeURIComponent(
      `stateToken=${stateToken}&nextUrl=${nextUrl}`
    );

    return (
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `response_type=code&` +
      `state=${STATE}&` +
      `access_type=${accessType}&` +
      `prompt=consent`
    );
  }

  /**
   * Verifies the provided state token against the expected HMAC hash.
   * The state token is split into the unique ID and the hash,
   * and the hash is recomputed to check its validity.
   *
   * @param {string} stateToken - The state token to verify.
   * @returns {boolean} True if the token is valid, false otherwise.
   */
  static verifyStateToken(stateToken: string): boolean {
    return StateTokenService.verify(stateToken);
  }
}
